import { DIFFICULTY_CONFIG } from '../config/game.config'

export interface GamePerformanceRecord {
  timestamp: number
  accuracy: number // 0-100: shots hit / shots fired
  survivalTimeMs: number // How long player survived
  score: number
  scorePerMinute: number
  aliensDestroyed: number
  totalAliens: number
  aliensDestroyedRatio: number // 0-100
  won: boolean
}

export interface SkillProfile {
  // Calculated skill score (0-100)
  skillScore: number
  // Current skill level name
  skillLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  // Total games played
  gamesPlayed: number
  // Recent game records for running calculations
  recentGames: GamePerformanceRecord[]
  // Lifetime averages
  lifetimeStats: {
    averageAccuracy: number
    averageSurvivalTimeMs: number
    averageScorePerMinute: number
    averageAliensDestroyedRatio: number
    totalWins: number
    totalLosses: number
  }
  // Last updated timestamp
  lastUpdated: number
}

const DEFAULT_PROFILE: SkillProfile = {
  skillScore: 50, // Start at intermediate
  skillLevel: 'intermediate',
  gamesPlayed: 0,
  recentGames: [],
  lifetimeStats: {
    averageAccuracy: 50,
    averageSurvivalTimeMs: 30000,
    averageScorePerMinute: 100,
    averageAliensDestroyedRatio: 50,
    totalWins: 0,
    totalLosses: 0,
  },
  lastUpdated: Date.now(),
}

export class PlayerSkillProfile {
  private profile: SkillProfile

  constructor() {
    this.profile = this.loadProfile()
  }

  private loadProfile(): SkillProfile {
    try {
      const stored = localStorage.getItem(DIFFICULTY_CONFIG.storageKey)
      if (stored) {
        const parsed = JSON.parse(stored) as SkillProfile
        // Validate the parsed profile has required fields
        if (this.isValidProfile(parsed)) {
          return parsed
        }
      }
    } catch (error) {
      console.warn('Failed to load skill profile, using defaults:', error)
    }
    return { ...DEFAULT_PROFILE }
  }

  private isValidProfile(profile: unknown): profile is SkillProfile {
    if (!profile || typeof profile !== 'object') return false
    const p = profile as Record<string, unknown>
    return (
      typeof p.skillScore === 'number' &&
      typeof p.skillLevel === 'string' &&
      typeof p.gamesPlayed === 'number' &&
      Array.isArray(p.recentGames) &&
      typeof p.lifetimeStats === 'object'
    )
  }

  private saveProfile(): void {
    try {
      localStorage.setItem(DIFFICULTY_CONFIG.storageKey, JSON.stringify(this.profile))
    } catch (error) {
      console.warn('Failed to save skill profile:', error)
    }
  }

  getProfile(): SkillProfile {
    return { ...this.profile }
  }

  getSkillScore(): number {
    return this.profile.skillScore
  }

  getSkillLevel(): SkillProfile['skillLevel'] {
    return this.profile.skillLevel
  }

  getGamesPlayed(): number {
    return this.profile.gamesPlayed
  }

  isProfileStable(): boolean {
    return this.profile.gamesPlayed >= DIFFICULTY_CONFIG.adaptation.gamesForStableProfile
  }

  recordGamePerformance(record: GamePerformanceRecord): void {
    // Add to recent games, keeping only the window size
    this.profile.recentGames.push(record)
    if (this.profile.recentGames.length > DIFFICULTY_CONFIG.adaptation.recentGamesWindow) {
      this.profile.recentGames.shift()
    }

    // Update games played
    this.profile.gamesPlayed++

    // Update lifetime stats
    this.updateLifetimeStats(record)

    // Recalculate skill score
    this.recalculateSkillScore()

    // Update timestamp
    this.profile.lastUpdated = Date.now()

    // Persist
    this.saveProfile()
  }

  private updateLifetimeStats(record: GamePerformanceRecord): void {
    const stats = this.profile.lifetimeStats
    const n = this.profile.gamesPlayed

    // Running average calculation
    stats.averageAccuracy = this.runningAverage(stats.averageAccuracy, record.accuracy, n)
    stats.averageSurvivalTimeMs = this.runningAverage(
      stats.averageSurvivalTimeMs,
      record.survivalTimeMs,
      n
    )
    stats.averageScorePerMinute = this.runningAverage(
      stats.averageScorePerMinute,
      record.scorePerMinute,
      n
    )
    stats.averageAliensDestroyedRatio = this.runningAverage(
      stats.averageAliensDestroyedRatio,
      record.aliensDestroyedRatio,
      n
    )

    if (record.won) {
      stats.totalWins++
    } else {
      stats.totalLosses++
    }
  }

  private runningAverage(currentAvg: number, newValue: number, n: number): number {
    return currentAvg + (newValue - currentAvg) / n
  }

  private recalculateSkillScore(): void {
    const weights = DIFFICULTY_CONFIG.weights
    const recentGames = this.profile.recentGames

    if (recentGames.length === 0) {
      return
    }

    // Calculate weighted averages from recent games
    const avgAccuracy =
      recentGames.reduce((sum, g) => sum + g.accuracy, 0) / recentGames.length
    const avgSurvivalTime =
      recentGames.reduce((sum, g) => sum + g.survivalTimeMs, 0) / recentGames.length
    const avgScorePerMinute =
      recentGames.reduce((sum, g) => sum + g.scorePerMinute, 0) / recentGames.length
    const avgAliensDestroyed =
      recentGames.reduce((sum, g) => sum + g.aliensDestroyedRatio, 0) / recentGames.length

    // Normalize metrics to 0-100 scale
    // Accuracy is already 0-100
    const normalizedAccuracy = Math.min(100, Math.max(0, avgAccuracy))

    // Survival time: 0-120 seconds maps to 0-100
    const normalizedSurvival = Math.min(100, Math.max(0, (avgSurvivalTime / 120000) * 100))

    // Score per minute: 0-300 maps to 0-100 (assuming max ~300 score/min for expert)
    const normalizedScorePM = Math.min(100, Math.max(0, (avgScorePerMinute / 300) * 100))

    // Aliens destroyed ratio is already 0-100
    const normalizedAliens = Math.min(100, Math.max(0, avgAliensDestroyed))

    // Calculate weighted skill score
    const newSkillScore =
      normalizedAccuracy * weights.accuracy +
      normalizedSurvival * weights.survivalTime +
      normalizedScorePM * weights.scorePerMinute +
      normalizedAliens * weights.aliensDestroyedRatio

    // Apply smoothing based on profile stability
    const adjustmentRate = this.isProfileStable()
      ? DIFFICULTY_CONFIG.adaptation.adjustmentRate
      : DIFFICULTY_CONFIG.adaptation.adjustmentRate * 2 // Faster adjustment for new players

    this.profile.skillScore =
      this.profile.skillScore + (newSkillScore - this.profile.skillScore) * adjustmentRate

    // Clamp to 0-100
    this.profile.skillScore = Math.min(100, Math.max(0, this.profile.skillScore))

    // Update skill level
    this.profile.skillLevel = this.calculateSkillLevel(this.profile.skillScore)
  }

  private calculateSkillLevel(score: number): SkillProfile['skillLevel'] {
    const levels = DIFFICULTY_CONFIG.skillLevels
    if (score < levels.beginner.max) return 'beginner'
    if (score < levels.intermediate.max) return 'intermediate'
    if (score < levels.advanced.max) return 'advanced'
    return 'expert'
  }

  resetProfile(): void {
    this.profile = { ...DEFAULT_PROFILE }
    this.saveProfile()
  }
}

// Singleton instance
export const playerSkillProfile = new PlayerSkillProfile()
